<template>
  <div class="space-y-8">
    <!-- Welcome Section -->
    <div class="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 rounded-3xl p-8 text-white relative overflow-hidden">
      <div class="absolute inset-0 bg-black/10"></div>
      <div class="relative z-10">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">¡Bienvenido, {{ authStore.user?.name }}! 👋</h1>
            <p class="text-blue-100 text-lg">Gestiona tu sistema de mascotas desde aquí</p>

          </div>
          <div class="hidden lg:block">
            <div class="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
              <svg class="h-16 w-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <!-- Total Users Card -->
      <div class="stat-card stat-card-blue">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-blue-100 text-sm font-medium uppercase tracking-wide">Total Usuarios</p>
            <p class="text-3xl font-bold mt-2">
              <span v-if="loading" class="animate-pulse bg-white/20 rounded h-8 w-16 inline-block"></span>
              <span v-else>{{ stats.totalUsers }}</span>
            </p>
            <div class="flex items-center mt-2">
              <svg class="h-4 w-4 text-green-300 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-blue-100">+12% este mes</span>
            </div>
          </div>
          <div class="bg-white/20 rounded-2xl p-3">
            <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Total Pets Card -->
      <div class="stat-card stat-card-green">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-green-100 text-sm font-medium uppercase tracking-wide">Total Mascotas</p>
            <p class="text-3xl font-bold mt-2">
              <span v-if="loading" class="animate-pulse bg-white/20 rounded h-8 w-16 inline-block"></span>
              <span v-else>{{ stats.totalPets }}</span>
            </p>
            <div class="flex items-center mt-2">
              <svg class="h-4 w-4 text-green-300 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-green-100">+8% este mes</span>
            </div>
          </div>
          <div class="bg-white/20 rounded-2xl p-3">
            <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Animal Types Card -->
      <div class="stat-card stat-card-purple">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-purple-100 text-sm font-medium uppercase tracking-wide">Tipos de Animales</p>
            <p class="text-3xl font-bold mt-2">
              <span v-if="loading" class="animate-pulse bg-white/20 rounded h-8 w-16 inline-block"></span>
              <span v-else>{{ stats.animalTypes }}</span>
            </p>
            <div class="flex items-center mt-2">
              <svg class="h-4 w-4 text-purple-300 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-purple-100">Estable</span>
            </div>
          </div>
          <div class="bg-white/20 rounded-2xl p-3">
            <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Breeds Card -->
      <div class="stat-card stat-card-orange">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-orange-100 text-sm font-medium uppercase tracking-wide">Razas Registradas</p>
            <p class="text-3xl font-bold mt-2">
              <span v-if="loading" class="animate-pulse bg-white/20 rounded h-8 w-16 inline-block"></span>
              <span v-else>{{ stats.breeds }}</span>
            </p>
            <div class="flex items-center mt-2">
              <svg class="h-4 w-4 text-orange-300 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-orange-100">+5% este mes</span>
            </div>
          </div>
          <div class="bg-white/20 rounded-2xl p-3">
            <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Overview -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Recent Activity -->
      <div class="modern-card">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-gray-900">Actividad Reciente</h2>
          <div class="flex items-center space-x-2">
            <div class="h-3 w-3 bg-green-400 rounded-full animate-pulse"></div>
            <span class="text-sm text-gray-500">En vivo</span>
          </div>
        </div>
        <div class="space-y-4">
          <!-- Loading State -->
          <div v-if="activityLoading" class="space-y-3">
            <div v-for="i in 5" :key="i" class="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 animate-pulse">
              <div class="h-8 w-8 bg-gray-200 rounded-full"></div>
              <div class="flex-1 space-y-2">
                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                <div class="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
          
          <!-- Empty State -->
          <div v-else-if="recentActivity.length === 0" class="text-center py-8">
            <svg class="h-12 w-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p class="text-gray-500">No hay actividad reciente</p>
          </div>
          
          <!-- Activity List -->
          <div v-else v-for="activity in recentActivity" :key="activity.id" class="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
            <div class="flex-shrink-0">
              <div class="h-8 w-8 rounded-full flex items-center justify-center" :class="getActivityIconClass(activity.action).iconBg">
                <svg class="h-4 w-4" :class="getActivityIconClass(activity.action).iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getActivityIcon(activity.action)"></path>
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ activity.description }}</p>
              <div class="flex items-center mt-1 space-x-2">
                <p class="text-xs text-gray-500">{{ formatDate(activity.createdAt) }}</p>
                <span class="text-xs text-gray-300">•</span>
                <p class="text-xs text-gray-500 capitalize">{{ activity.entity }}</p>
                <span class="text-xs text-gray-300">•</span>
                <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" :class="getSourceBadgeClass(activity.source)">
                  {{ activity.source?.toUpperCase() || 'WEB' }}
                </span>
                <span v-if="activity.user" class="text-xs text-gray-300">•</span>
                <p v-if="activity.user" class="text-xs text-gray-500">{{ activity.user.name }}</p>
              </div>
            </div>
            <div v-if="!activity.isRead" class="flex-shrink-0">
              <div class="w-2 h-2 bg-blue-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Users (Admin Only) -->
      <div v-if="authStore.user?.role === 'admin'" class="modern-card">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-gray-900">Usuarios Recientes</h2>
          <router-link to="/users" class="btn btn-primary btn-sm">
            Ver todos
          </router-link>
        </div>
        <div class="space-y-4">
          <div v-for="user in recentUsers" :key="user.id" class="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
            <div class="flex-shrink-0">
              <div class="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <span class="text-sm font-bold text-white">{{ user.name.charAt(0).toUpperCase() }}</span>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ user.name }}</p>
              <p class="text-sm text-gray-500 truncate">{{ user.email }}</p>
            </div>
            <div class="flex-shrink-0">
              <span class="badge" :class="{
                'badge-success': user.status === 'active',
                'badge-danger': user.status === 'inactive'
              }">
                {{ user.status === 'active' ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUsersStore } from '@/stores/users'
import { userService } from '@/services/userService'
import { petService } from '@/services/petService'
import { animalTypeService } from '@/services/animalTypeService'
import { breedService } from '@/services/breedService'
import { activityService } from '@/services/activityService'

const authStore = useAuthStore()
const usersStore = useUsersStore()

// Reactive stats data
const stats = reactive({
  totalUsers: 0,
  totalPets: 0,
  animalTypes: 0,
  breeds: 0
})

const loading = ref(true)

// Recent activity data
const recentActivity = ref([])
const activityLoading = ref(false)
let activityInterval = null

// Computed properties
const recentUsers = computed(() => {
  return usersStore.users.slice(0, 5)
})

// Methods
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getActivityIcon = (action) => {
  switch (action) {
    case 'created':
      return 'M12 6v6m0 0v6m0-6h6m-6 0H6'
    case 'updated':
      return 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
    case 'deleted':
      return 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
    default:
      return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
  }
}

const getActivityIconClass = (action) => {
  switch (action) {
    case 'created':
      return { iconBg: 'bg-green-100', iconColor: 'text-green-600' }
    case 'updated':
      return { iconBg: 'bg-blue-100', iconColor: 'text-blue-600' }
    case 'deleted':
      return { iconBg: 'bg-red-100', iconColor: 'text-red-600' }
    default:
      return { iconBg: 'bg-gray-100', iconColor: 'text-gray-600' }
  }
}

const getSourceBadgeClass = (source) => {
  switch (source) {
    case 'api':
      return 'bg-purple-100 text-purple-800'
    case 'web':
    default:
      return 'bg-indigo-100 text-indigo-800'
  }
}

const loadRecentActivity = async () => {
  try {
    activityLoading.value = true
    const response = await activityService.getRecentActivities(5)
    recentActivity.value = response.data
  } catch (error) {
    console.error('Error loading recent activity:', error)
  } finally {
    activityLoading.value = false
  }
}



// Load dashboard data
const loadDashboardData = async () => {
  try {
    loading.value = true
    
    // Load stats in parallel
    const [userStats, petStats, animalTypeStats, breedStats] = await Promise.all([
      userService.getUserStats(),
      petService.getPetStats(),
      animalTypeService.getAnimalTypeStats(),
      breedService.getBreedStats()
    ])
    
    // Update stats
    stats.totalUsers = userStats.data?.total || 0
    stats.totalPets = petStats.data?.total || 0
    stats.animalTypes = animalTypeStats.data?.total || 0
    stats.breeds = breedStats.data?.total || 0
    
    // Load recent activity
    await loadRecentActivity()
    
  } catch (error) {
    console.error('Error loading dashboard data:', error)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadDashboardData()
  if (authStore.user?.role === 'admin') {
    usersStore.fetchUsers()
  }
  
  // Set up auto-refresh for recent activities every 30 seconds
  activityInterval = setInterval(() => {
    loadRecentActivity()
  }, 30000)
})

onUnmounted(() => {
  if (activityInterval) {
    clearInterval(activityInterval)
  }
})
</script>